/**
 * Heurísticas de qualidade de rede (Network Information API).
 * Usado no Lobby (aviso pré-entrada) e na Room (degradar vídeo cedo).
 */

export type NetworkTier = "good" | "fair" | "poor";

export interface NetworkSnapshot {
    tier: NetworkTier;
    effectiveType: string | null;
    rtt: number | null;
    downlink: number | null;
}

type NavConnection = {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    addEventListener?: (type: string, cb: () => void) => void;
    removeEventListener?: (type: string, cb: () => void) => void;
};

export function getNavigatorConnection(): NavConnection | null {
    if (typeof navigator === "undefined") return null;
    return (
        (navigator as unknown as { connection?: NavConnection }).connection ?? null
    );
}

export function evaluateNetworkQuality(): NetworkSnapshot | null {
    const connection = getNavigatorConnection();
    if (!connection) return null;

    const effectiveType = connection.effectiveType ?? null;
    const rtt = typeof connection.rtt === "number" ? connection.rtt : null;
    const downlink =
        typeof connection.downlink === "number" ? connection.downlink : null;

    const isVerySlow =
        effectiveType === "slow-2g" ||
        effectiveType === "2g" ||
        (rtt != null && rtt > 900) ||
        (downlink != null && downlink > 0 && downlink < 0.35);

    const isFair =
        effectiveType === "3g" ||
        (rtt != null && rtt > 450) ||
        (downlink != null && downlink > 0 && downlink < 0.75);

    const tier: NetworkTier = isVerySlow ? "poor" : isFair ? "fair" : "good";

    return { tier, effectiveType, rtt, downlink };
}

export function networkWarningMessage(snapshot: NetworkSnapshot): string | null {
    if (snapshot.tier === "poor") {
        return "Rede muito lenta (2G): câmera desligada automaticamente para manter o áudio estável. Prefira Wi‑Fi e mantenha o app em primeiro plano.";
    }
    if (snapshot.tier === "fair") {
        return "Conexão 3G ou instável: vídeo reduzido para evitar queda. Wi‑Fi estável mantém você na sala com mais qualidade.";
    }
    return null;
}

/** Limites de envio de vídeo por tier — prioriza áudio (não saturar uplink). */
export function videoConstraintsForTier(tier: NetworkTier): {
    width: number;
    height: number;
    frameRate: number;
    audioOnly: boolean;
} {
    if (tier === "poor") {
        return { width: 0, height: 0, frameRate: 0, audioOnly: true };
    }
    if (tier === "fair") {
        return { width: 480, height: 360, frameRate: 15, audioOnly: false };
    }
    return { width: 960, height: 540, frameRate: 24, audioOnly: false };
}

/** Defaults de entrada: celular começa mais leve para não saturar uplink cedo. */
export function joinVideoDefaults(isMobileDevice: boolean): {
    width: number;
    height: number;
    frameRate: number;
    maxBitrateHigh: number;
    maxBitrateLow: number;
} {
    if (isMobileDevice) {
        return {
            width: 640,
            height: 360,
            frameRate: 20,
            maxBitrateHigh: 400_000,
            maxBitrateLow: 100_000,
        };
    }
    return {
        width: 960,
        height: 540,
        frameRate: 24,
        maxBitrateHigh: 600_000,
        maxBitrateLow: 150_000,
    };
}

export function subscribeNetworkChanges(onChange: () => void): () => void {
    const connection = getNavigatorConnection();
    if (!connection?.addEventListener) {
        return () => {};
    }
    connection.addEventListener("change", onChange);
    return () => connection.removeEventListener?.("change", onChange);
}
